import { useCallback, useEffect, useRef, useState } from "react";
import type { RequestStatus } from "../../auth";
import { listUsers } from "../services/usersService";
import type {
  AccountType,
  AdminUser,
  RosterCounts,
  UserTabId,
} from "../types/users.types";

/** How many rows a page of the roster holds. */
export const USERS_PER_PAGE = 10;

interface UseUsersOptions {
  page: number;
  tab: UserTabId;
  accountType: AccountType;
  search: string;
}

/**
 * One page of the roster, plus the counts the tabs carry.
 *
 * The counts used to be a second request of their own. They are not any more:
 * the list endpoint sends them with every page under `tab_counts`, and they
 * describe the roster rather than the query, so taking them from the answer
 * already in hand is one request instead of two for the same figures. A failed
 * load leaves them at their last known value rather than zeroing them — a
 * count that could not be fetched is not a count of none.
 *
 * Two more things are worth pointing at:
 *
 * - **Rows already in hand are left alone while the next page is fetched.** A
 *   table that empties itself on every keystroke flickers, and the search box
 *   is the thing that fires most often. The old page stays until the new one
 *   lands, and `status` is what the screen shimmers off.
 *
 * - **A late answer to a superseded request is dropped.** Type "an", then "ani":
 *   two requests are out, and nothing guarantees they come back in order. The
 *   request id below is what makes the last one asked for the one that wins,
 *   rather than the last one to arrive.
 */
export function useUsers({
  page,
  tab,
  accountType,
  search,
}: UseUsersOptions) {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState<RequestStatus>("loading");
  const [counts, setCounts] = useState<RosterCounts | null>(null);

  const requestId = useRef(0);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setStatus("loading");

    try {
      const result = await listUsers({
        page,
        pageSize: USERS_PER_PAGE,
        tab,
        accountType,
        search,
      });

      if (id !== requestId.current) return;
      setUsers(result.users);
      setTotal(result.total);
      // Sent with every page and describing the whole roster either way, so
      // they are simply taken from whichever answer arrived last.
      setCounts(result.counts);
      setStatus("ready");
    } catch {
      if (id !== requestId.current) return;
      setStatus("failed");
    }
  }, [page, tab, accountType, search]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    users,
    total,
    counts,
    status,
    totalPages: Math.max(1, Math.ceil(total / USERS_PER_PAGE)),
    reload: load,
  };
}

export default useUsers;
